import { ReactNode, useCallback, useContext, useEffect, useId, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { ReactComponent as BuildingIcon } from "assets/images/common/building.svg";
import { ReactComponent as CourseUpIcon } from "assets/images/common/course-up.svg";
import { ReactComponent as CourseDownIcon } from "assets/images/common/grow-down.svg";
import { ReactComponent as IncidentsIcon } from "assets/images/common/incidents.svg";
import { ReactComponent as InfoIcon } from "assets/images/common/info.svg";
import { ReactComponent as LongArrowIcon } from "assets/images/common/long-arrow.svg";
import { ReactComponent as NotFoundIcon } from "assets/images/common/not-found.svg";
import { ReactComponent as PlusIcon } from "assets/images/common/plus.svg";
import { ReactComponent as PortfolioIcon } from "assets/images/common/portfolio.svg";
import { ReactComponent as ShieldIcon } from "assets/images/common/shield.svg";

import useDebounce from "hooks/useDebounce";
import useRealtime from "hooks/useRealtime";
import PageTemplate from "modules/common/PageTemplate";

import AsyncSearchableSelect from "components/AsyncSearchableSelect";
import Button from "components/Button";
import Card from "components/Card";
import InfoCard from "components/InfoCard";
import Pagination from "components/Pagination";
import SearchInput from "components/SearchInput";

import { RootStore } from "store";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { setModalCallbacks } from "store/slices/modalCallbacksRegistry";
import { hideModal, showModal } from "store/slices/modalSlice";
import {
  createPortfolioRequest,
  getPortfolioListRequest,
  getPortfolioStatisticsRequest,
  resetPortfolioList,
  resetPortfolioStatistics,
  resetSuccessfullyAddedPortfolio,
  resetSuccessfullyRemovedPortfolio,
  resetSuccessfullyUpdatedPortfolio,
  updatePortfolioRequest,
} from "store/slices/portfolioSlice";
import { showToast } from "store/slices/toastSlice";

import PortfolioCard from "modules/companies/PortfolioCard";

import { LoaderContext } from "context/GlobalLoader";

import { QUERY_PARAMS } from "utils/constants";
import { QueryHelper } from "utils/helpers";
import { PAGES } from "utils/links";

import { BaseOption, ModalType, UserRole } from "entities/common";
import { CreatePortfolioRequest, PortfolioItem, UpdatePortfolioRequest } from "entities/portfolios";
import { PortfolioFormValues } from "widgets/Modal/CreatePortfolio/Content";

import { CompaniesApi } from "services/http";

import styles from "./style.module.scss";

type StatInfo = {
  title: string;
  value: string | number;
  subtitle: string;
  color: string;
  icon?: ReactNode;
  tooltip?: string;
};

const formatImprovement = (value: number | null | undefined): string => {
  if (value == null) return "-";
  if (value > 0) return `+${value}%`;
  return `${value}%`;
};

const buildListUrl = (search: string, companyFilter: BaseOption, page: number, isAdmin: boolean): string =>
  `${PAGES.PORTFOLIOS_LIST}${QueryHelper.getQueryString({
    [QUERY_PARAMS.SEARCH]: search.trim() || undefined,
    ...(isAdmin && companyFilter.value !== "all"
      ? { [QUERY_PARAMS.COMPANY_ID]: companyFilter.value, [QUERY_PARAMS.COMPANY_LABEL]: companyFilter.label }
      : {}),
    [QUERY_PARAMS.PAGE]: page > 1 ? page : undefined,
  })}`;

const PortfolioListPage = () => {
  const callbackKey = useId();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const {
    items: portfolios,
    isLoading,
    statistics,
    isStatisticsLoading,
    meta,
    successfullyAddedId,
    successfullyUpdatedId,
    successfullyRemovedId,
  } = useAppSelector((state: RootStore) => state.portfolios);
  const { handleLoad } = useContext(LoaderContext);
  const isAdmin = useMemo(() => user?.role === UserRole.ADMINISTRATOR, [user]);

  const location = useLocation();
  const [search, setSearch] = useState(
    () => new URLSearchParams(location.search).get(QUERY_PARAMS.SEARCH) || "",
  );
  const [companyFilter, setCompanyFilter] = useState<BaseOption>(() => {
    const params = new URLSearchParams(location.search);
    const value = params.get(QUERY_PARAMS.COMPANY_ID);
    return value
      ? { value, label: params.get(QUERY_PARAMS.COMPANY_LABEL) || value }
      : { value: "all", label: "All companies" };
  });
  const [page, setPage] = useState(() => Number(new URLSearchParams(location.search).get(QUERY_PARAMS.PAGE)) || 1);
  const debouncedSearch = useDebounce(search, 500);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleSave = useCallback(
    (values: unknown) => {
      const data = values as PortfolioFormValues;

      const body: CreatePortfolioRequest = {
        portfolio_name: data.name,
        company_id: Number(data.companyId),
        description: data.description,
        city: data.city,
        area: data.area,
        building_name: data.buildingName,
        unit_number: data.unitNumber,
        street: data.street,
        manager_id: Number(data.managerId),
      };

      dispatch(createPortfolioRequest({ body, images: data.images }));
    },
    [dispatch],
  );

  const handleCreatePortfolio = useCallback(() => {
    setModalCallbacks(callbackKey, { onSave: handleSave });

    dispatch(
      showModal({
        modalType: ModalType.CREATE_PORTFOLIO,
        modalProps: {
          mode: "create",
          callbackKey,
          isShowCompanySelect: isAdmin,
          companyId: isAdmin ? undefined : user?.company?.id,
        },
      }),
    );
  }, [dispatch, callbackKey, handleSave, isAdmin, user?.company?.id]);

  // TODO remove
  const handleEditPortfolio = useCallback(
    (id: number) => (values: unknown) => {
      const data = values as PortfolioFormValues;

      const body: UpdatePortfolioRequest = {
        portfolio_name: data.name,
        description: data.description,
        city: data.city,
        area: data.area,
        building_name: data.buildingName,
        unit_number: data.unitNumber,
        street: data.street,
        manager_id: Number(data.managerId),
      };

      dispatch(updatePortfolioRequest({ id: id, body }));
    },
    [dispatch],
  );

  const handlePortfolioClick = useCallback(
    (portfolio: PortfolioItem) => () => {
      navigate(`${PAGES.PORTFOLIO_DETAIL.replace(":id", portfolio.id.toString())}`, {
        state: { from: buildListUrl(search, companyFilter, page, isAdmin) },
      });
      // setModalCallbacks(callbackKey, { onSave: handleEditPortfolio(portfolio.id) });

      // TODO remove
      // dispatch(
      //   showModal({
      //     modalType: ModalType.CREATE_PORTFOLIO,
      //     modalProps: {
      //       mode: "edit",
      //       portfolioId: portfolio.id,
      //       callbackKey,
      //       initialData: {
      //         name: portfolio?.name || "",
      //         companyId: portfolio?.company_id?.toString() || "",
      //         description: portfolio?.description || "",
      //         address: portfolio?.address || "",
      //         managerId: portfolio?.manager_id?.toString() || "",
      //         images: portfolio?.images || [],
      //       },
      //     },
      //   }),
      // );
    },
    [navigate, search, companyFilter, page, isAdmin],
  );

  const handleRequestPortfolios = useCallback(
    (debouncedSearch: string, page: number, companyId?: string, silent = false) => {
      dispatch(
        getPortfolioListRequest(
          QueryHelper.getQueryString({
            [QUERY_PARAMS.PAGE]: page,
            [QUERY_PARAMS.PAGE_SIZE]: 12,
            [QUERY_PARAMS.COMPANY_ID]: isAdmin ? undefined : user?.company?.id,
            [QUERY_PARAMS.SEARCH]: debouncedSearch.trim(),
            ...(companyId && companyId !== "all" ? { [QUERY_PARAMS.COMPANY_ID]: Number(companyId) } : {}),
          }),
          silent ? { silent: true } : undefined,
        ),
      );
    },
    [dispatch, isAdmin, user?.company?.id],
  );

  const handlePageChange = useCallback((page: number) => {
    setPage(page);
  }, []);

  const handleCompanyChange = useCallback((value: string, label: string) => {
    setCompanyFilter({ value, label });
    setPage(1);
  }, []);

  const handleCompanySearch = useCallback(
    async (search: string, page: number) => {
      try {
        const { data } = await CompaniesApi.getList(
          QueryHelper.getQueryString({
            [QUERY_PARAMS.SEARCH]: search || undefined,
            [QUERY_PARAMS.PAGE]: page,
            [QUERY_PARAMS.PAGE_SIZE]: 50,
          }),
        );

        return {
          options: [
            { value: "all", label: "All companies" },
            ...data?.items?.map((company) => ({ value: String(company?.id), label: company?.name })),
          ],
          hasMore: data?.total ? page * 50 < data?.total : false,
        };
      } catch {
        return { options: [], hasMore: false };
      }
    },
    [dispatch],
  );

  const statisticsInfo: StatInfo[] = useMemo(() => {
    const level = statistics?.compliance_score_level;
    const improvementLevel = statistics?.compliance_improvement_level;
    const avgIncidents = statistics?.avg_incidents_per_month;

    return [
      {
        title: "Compliance Score",
        value: level && level !== "insufficient" ? `${statistics?.compliance_score}%` : "-",
        subtitle:
          level && level !== "insufficient"
            ? `${level === "high" ? "High" : level === "medium" ? "Medium" : "Low"} compliance`
            : "Insufficient data",
        color: level === "high" ? "green" : level === "medium" ? "orange" : level === "low" ? "red" : "gray",
        icon: <ShieldIcon />,
        tooltip: "Per DM-HSD-GU119.",
      },
      {
        title: "Compliance improvement",
        value:
          improvementLevel && improvementLevel !== "insufficient"
            ? formatImprovement(statistics?.compliance_improvement)
            : "-",
        subtitle:
          !improvementLevel || improvementLevel === "insufficient" ? "Insufficient data" : "Vs previous 30 days",
        color: improvementLevel === "up" ? "green" : improvementLevel === "down" ? "red" : "gray",
        icon:
          improvementLevel === "up" ? (
            <CourseUpIcon />
          ) : improvementLevel === "down" ? (
            <CourseDownIcon />
          ) : improvementLevel === "flat" ? (
            <LongArrowIcon />
          ) : undefined,
        tooltip: "Change in the compliance score compared to the previous 30 days, across all portfolios in scope.",
      },
      {
        title: "Avg incidents/month",
        value: avgIncidents == null ? "-" : `${avgIncidents}`,
        subtitle: avgIncidents == null ? "Insufficient data" : "Last 3 months",
        color: avgIncidents == null ? "gray" : avgIncidents === 0 ? "green" : avgIncidents < 4 ? "orange" : "red",
        icon: <IncidentsIcon />,
      },
      {
        title: "Total portfolios",
        value: statistics?.total_portfolios?.toString() || 0,
        subtitle: isAdmin ? "All companies" : "Across company",
        color: "blue",
        icon: <PortfolioIcon />,
      },
      {
        title: "Total assets",
        value: statistics?.total_assets?.toString() || 0,
        subtitle: "In all portfolios",
        color: "violet",
        icon: <BuildingIcon />,
      },
    ];
  }, [
    statistics?.compliance_score,
    statistics?.compliance_score_level,
    statistics?.compliance_improvement,
    statistics?.compliance_improvement_level,
    statistics?.avg_incidents_per_month,
    statistics?.total_assets,
    statistics?.total_portfolios,
    isAdmin,
  ]);

  useEffect(() => {
    dispatch(getPortfolioStatisticsRequest());
  }, [dispatch, successfullyAddedId, successfullyUpdatedId, successfullyRemovedId]);

  // keep the URL in sync so the state can be restored when returning from a portfolio
  useEffect(() => {
    navigate(buildListUrl(debouncedSearch, companyFilter, page, isAdmin), { replace: true });
  }, [navigate, debouncedSearch, companyFilter, page, isAdmin]);

  // request portfolios when search, page, or company filter change
  useEffect(() => {
    handleRequestPortfolios(debouncedSearch, page, companyFilter.value);
  }, [
    handleRequestPortfolios,
    debouncedSearch,
    page,
    companyFilter,
    successfullyAddedId,
    successfullyUpdatedId,
    successfullyRemovedId,
  ]);

  const reload = useCallback(() => {
    handleRequestPortfolios(debouncedSearch, page, companyFilter.value, true);
    dispatch(getPortfolioStatisticsRequest({ silent: true }));
  }, [handleRequestPortfolios, debouncedSearch, page, companyFilter.value, dispatch]);

  useRealtime(["compliance", "incidents"], reload);

  // loader
  useEffect(() => {
    handleLoad(isLoading);

    return () => {
      handleLoad(false);
    };
  }, [isLoading, handleLoad]);

  useEffect(() => {
    if (successfullyAddedId) {
      dispatch(resetSuccessfullyAddedPortfolio());
      dispatch(hideModal());
      dispatch(
        showToast({
          id: "successfully-added-portfolio",
          message: "The portfolio was successfully created.",
          type: "success",
          duration: 3000,
        }),
      );
    }
  }, [successfullyAddedId, dispatch]);

  useEffect(() => {
    if (successfullyUpdatedId) {
      dispatch(resetSuccessfullyUpdatedPortfolio());
      dispatch(hideModal());
      dispatch(
        showToast({
          id: "successfully-updated-portfolio",
          message: "The portfolio was successfully updated.",
          type: "success",
          duration: 3000,
        }),
      );
    }
  }, [successfullyUpdatedId, dispatch]);

  useEffect(() => {
    if (successfullyRemovedId) {
      dispatch(resetSuccessfullyRemovedPortfolio());
      dispatch(hideModal());
      dispatch(
        showToast({
          id: "successfully-removed-portfolio",
          message: "The portfolio was successfully deleted.",
          type: "success",
          duration: 3000,
        }),
      );
    }
  }, [successfullyRemovedId, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(resetPortfolioList());
      dispatch(resetPortfolioStatistics());
    };
  }, [dispatch]);

  return (
    <PageTemplate
      title="Portfolios"
      description="Manage your real estate portfolios and assets"
      actions={
        <Button variant="primary" onClick={handleCreatePortfolio} className={styles.addButton}>
          <span className={styles.addButtonText}>Add portfolio</span>
          <PlusIcon className={styles.addButtonIcon} />
        </Button>
      }
      className={styles.list}
    >
      <div>
        {(portfolios?.length || (!portfolios?.length && (debouncedSearch || companyFilter.value !== "all"))) && (
          <div className={styles.stats}>
            {statisticsInfo.map((statistic) => (
              <InfoCard
                key={statistic.title}
                color={statistic.color as "gray"}
                size="basic"
                isLoading={isStatisticsLoading}
              >
                <div className={styles.statItem}>
                  <div className={styles.statHeader}>
                    <span className={styles.statTitle}>{statistic.title}</span>
                    {statistic.icon && (
                      <span className={`${styles.statIcon} ${styles[`statIcon--${statistic.color}`]}`}>
                        {statistic.icon}
                      </span>
                    )}
                  </div>

                  <div className={styles.statBody}>
                    <div className={styles.statValueRow}>
                      <span className={styles.statValue}>{statistic.value}</span>

                      {statistic.tooltip && (
                        <span className={styles.statInfo} tabIndex={0}>
                          <InfoIcon />
                          <span className={styles.statTooltip}>{statistic.tooltip}</span>
                        </span>
                      )}
                    </div>

                    <span className={styles.statSubtitle}>{statistic.subtitle}</span>
                  </div>
                </div>
              </InfoCard>
            ))}
          </div>
        )}

        <Card className={styles.contentCard}>
          <div className={styles.filters}>
            <SearchInput
              name="search"
              value={search}
              onChange={handleSearchChange}
              className={styles.searchInput}
              placeholder="Search portfolios by name or description..."
            />

            {isAdmin && (
              <AsyncSearchableSelect
                value={companyFilter.value}
                selectedLabel={companyFilter.label}
                onChange={handleCompanyChange}
                onSearch={handleCompanySearch}
                className={styles.companyFilter}
              />
            )}
          </div>

          {portfolios.length > 0 ? (
            <div className={styles.grid}>
              {portfolios.map((portfolio, index) => (
                <PortfolioCard
                  data={portfolio}
                  isShowCompanyName={isAdmin}
                  onClick={handlePortfolioClick(portfolio)}
                  key={`portfolio-${index}-${portfolio?.id}`}
                />
              ))}
            </div>
          ) : (
            <div className={styles.empty}>
              <NotFoundIcon />

              <p>{!statistics?.total_portfolios ? "No portfolios yet" : "No portfolios found"}</p>

              {!statistics?.total_portfolios && (
                <Button variant="secondary" onClick={handleCreatePortfolio}>
                  Add first portfolio
                </Button>
              )}
            </div>
          )}

          {meta?.total_pages > 1 && (
            <Pagination
              total={meta?.total_pages}
              page={page}
              onPageChange={handlePageChange}
              variant="secondary"
            />
          )}
        </Card>
      </div>
    </PageTemplate>
  );
};

export default PortfolioListPage;
